import { Contenuto, StatoContenuto } from '../types';
import { isPost } from './tipoContenuto';

/**
 * Quanti contenuti restano da scrivere a un cliente questo mese.
 *
 * Il calcolo era copia-incollato in tre punti (Dashboard e due volte in
 * Copywriter) e ogni modifica rischiava di allinearne solo uno: qui c'è la
 * sola versione buona.
 *
 * Regole:
 *  - contano i contenuti CREATI o PUBBLICATI nel mese;
 *  - contano anche quelli rimasti indietro dai mesi prima: testo confermato,
 *    ancora in lavorazione (non pubblicato né archiviato) e senza una data
 *    d'uscita nel mese o dopo. Un testo pronto e mai registrato uscirà adesso,
 *    quindi copre un posto di questo mese (caso Nectar, 02/10/2026). Quelli
 *    per campagne ADV restano fuori;
 *  - una BOZZA non confermata NON riempie il target: il copywriter deve ancora
 *    finirla, quindi resta "da scrivere";
 *  - se i testi li scrive il cliente, per noi non c'è niente da scrivere: 0;
 *  - i post grafici restano fuori: il target è di reel.
 */

export interface CalcoloDaScrivere {
  /** Tutti i contenuti (vengono filtrati per cliente qui dentro). */
  contenuti: Contenuto[];
  clienteId: string;
  /** Reel previsti al mese per quel cliente. */
  targetMensile: number;
  /** true = i testi li scrive il cliente (spunta nella scheda cliente). */
  testiDalCliente?: boolean;
  mese: number;
  anno: number;
}

/** Contenuti che "riempiono" il target del mese per quel cliente. */
export const contenutiDelMese = (
  contenuti: Contenuto[],
  clienteId: string,
  mese: number,
  anno: number
): number =>
  contenuti.filter((c) => {
    if (c.clienteId !== clienteId) return false;
    if (c.stato === StatoContenuto.BOZZA) return false;
    // Il target mensile è di reel: i post grafici non lo riempiono
    if (isPost(c)) return false;

    const creazione = new Date(c.createdAt);
    const creatoQuestoMese = creazione.getMonth() === mese && creazione.getFullYear() === anno;

    let pubblicatoQuestoMese = false;
    if (c.dataPubblicazione) {
      const pubblicazione = new Date(c.dataPubblicazione);
      pubblicatoQuestoMese =
        pubblicazione.getMonth() === mese && pubblicazione.getFullYear() === anno;
    }

    if (creatoQuestoMese || pubblicatoQuestoMese) return true;

    // Rimasto indietro: creato prima del mese, ancora in lavorazione e senza
    // un'uscita già fissata da questo mese in poi (lì verrà contato).
    const inizioMese = new Date(anno, mese, 1);
    const inLavorazione = c.stato !== StatoContenuto.PUBBLICATO && c.stato !== StatoContenuto.ARCHIVIATO;
    const uscitaDaOraInPoi = !!c.dataPubblicazione && new Date(c.dataPubblicazione) >= inizioMese;
    // I contenuti per le campagne ADV non sono il piano del mese.
    return inLavorazione && !c.campagnaAdv && creazione < inizioMese && !uscitaDaOraInPoi;
  }).length;

export const contenutiDaScrivere = ({
  contenuti,
  clienteId,
  targetMensile,
  testiDalCliente,
  mese,
  anno,
}: CalcoloDaScrivere): number => {
  // I testi se li scrive il cliente: il lato testo è coperto in partenza.
  if (testiDalCliente) return 0;
  return Math.max(0, targetMensile - contenutiDelMese(contenuti, clienteId, mese, anno));
};

/**
 * Post grafici del mese per un cliente (stesso criterio dei reel: creati o
 * pubblicati nel mese, bozze escluse). Il target mensile è quello settimanale
 * della scheda cliente moltiplicato per 4.
 */
export const postGraficiDelMese = (
  contenuti: Contenuto[],
  clienteId: string,
  mese: number,
  anno: number,
  // 03/11/2026: solo un formato ('grafica' = post grafici, 'foto' = caroselli foto); senza, tutti i post
  formato?: 'grafica' | 'foto'
): number =>
  contenuti.filter((c) => {
    if (c.clienteId !== clienteId || !isPost(c)) return false;
    if (formato && (c.formatoPost === 'foto' ? 'foto' : 'grafica') !== formato) return false;
    if (c.stato === StatoContenuto.BOZZA) return false;
    const creazione = new Date(c.createdAt);
    if (creazione.getMonth() === mese && creazione.getFullYear() === anno) return true;
    if (!c.dataPubblicazione) return false;
    const pubblicazione = new Date(c.dataPubblicazione);
    return pubblicazione.getMonth() === mese && pubblicazione.getFullYear() === anno;
  }).length;

export const targetPostGraficiMese = (postASettimana: number): number => Math.max(0, postASettimana) * 4;
